import { Handshake, MapPin, Target, UserRound, Users } from "lucide-react";
import { Link } from "react-router";
import {
  useCreatePath,
  useListContext,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Avatar as ContactAvatar } from "../contacts/Avatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import {
  CompanySpecializationBadges,
  MarketingConsentBadge,
  SalesPriorityBadge,
  SroMembershipBadge,
} from "./CompanySalesIntelligence";

export const CompanyCard = (props: { record?: Company }) => {
  const createPath = useCreatePath();
  const record = useRecordContext<Company>(props);
  const translate = useTranslate();
  const { companySectors } = useConfigurationContext();
  if (!record) return null;

  const sector = companySectors.find((s) => s.value === record.sector);
  const sectorLabel = sector?.label;
  const address = getCompanyAddress(record);
  const auditorsCount = record.size ? Math.round(record.size) : undefined;

  return (
    <Link
      to={createPath({
        resource: "companies",
        id: record.id,
        type: "show",
      })}
      className="no-underline"
    >
      <Card className="min-h-[290px] flex flex-col justify-between p-4 hover:bg-muted">
        <div className="flex min-w-0 flex-col items-center gap-1">
          {(record.lead_priority ||
            record.lead_score != null ||
            record.sro_membership_status === "ceased") && (
            <div className="mb-1 flex w-full items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1">
                <SalesPriorityBadge priority={record.lead_priority} />
                {record.sro_membership_status === "ceased" ? (
                  <SroMembershipBadge status={record.sro_membership_status} />
                ) : null}
              </div>
              {record.lead_score != null ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Target className="h-3.5 w-3.5" />
                  {record.lead_score}
                </span>
              ) : null}
            </div>
          )}
          <CompanyAvatar />
          <div className="text-center mt-1 min-w-0 w-full">
            <h6 className="text-sm font-medium leading-snug break-words [overflow-wrap:anywhere]">
              {record.name}
            </h6>
            <p className="text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
              {sectorLabel}
            </p>
          </div>
          {record.specializations?.length ? (
            <div className="mt-2 w-full">
              <CompanySpecializationBadges record={record} limit={2} />
            </div>
          ) : null}
          {record.lead_priority ? (
            <div className="mt-1 w-full">
              <MarketingConsentBadge record={record} />
            </div>
          ) : null}
          {(record.tax_identifier ||
            auditorsCount ||
            address ||
            record.decision_maker) && (
            <div className="mt-2 flex w-full min-w-0 flex-col gap-1 text-left">
              {record.tax_identifier && (
                <p className="text-xs leading-snug text-muted-foreground break-words [overflow-wrap:anywhere]">
                  <span className="font-medium text-foreground">
                    {translate(
                      "resources.companies.fields.tax_identifier_short",
                      {
                        _: "Tax ID",
                      },
                    )}
                    :
                  </span>{" "}
                  {record.tax_identifier}
                </p>
              )}
              {auditorsCount && (
                <div className="flex min-w-0 items-center gap-1 text-xs leading-snug text-muted-foreground">
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium text-foreground">
                    {translate("resources.companies.fields.size", {
                      _: "Size",
                    })}
                    :
                  </span>
                  <span>
                    {translate("resources.companies.auditors_count_value", {
                      smart_count: auditorsCount,
                      _: `${auditorsCount} auditors`,
                    })}
                  </span>
                </div>
              )}
              {record.decision_maker && (
                <div className="flex min-w-0 items-center gap-1 text-xs leading-snug text-muted-foreground">
                  <UserRound className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 truncate">
                    {record.decision_maker_role
                      ? `${record.decision_maker_role}: `
                      : ""}
                    {record.decision_maker}
                  </span>
                </div>
              )}
              {address && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex min-w-0 items-center gap-1 text-xs leading-snug text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 truncate">{address}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72 whitespace-normal break-words">
                    {address}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-row w-full justify-between gap-2 min-w-0">
          <div className="flex items-center">
            {record.nb_contacts ? (
              <ReferenceManyField reference="contacts" target="company_id">
                <AvatarGroupIterator />
              </ReferenceManyField>
            ) : null}
          </div>
          {record.nb_deals ? (
            <div className="flex items-center ml-2 gap-0.5 min-w-0">
              <Handshake className="w-4 h-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium">{record.nb_deals}</span>
              <span className="text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
                {translate("resources.deals.name", {
                  smart_count: record.nb_deals ?? 0,
                  _: "Deal |||| Deals",
                })}
              </span>
            </div>
          ) : null}
        </div>
      </Card>
    </Link>
  );
};

const getCompanyAddress = (record: Company) =>
  [
    record.address,
    [record.zipcode, record.city].filter(Boolean).join(" "),
    record.state_abbr,
    record.country,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

const AvatarGroupIterator = () => {
  const { data, total, error, isPending } = useListContext();
  if (isPending || error) return null;

  const MAX_AVATARS = 3;
  return (
    <div className="*:data-[slot=avatar]:ring-background flex -space-x-0.5 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:grayscale-50">
      {data.slice(0, MAX_AVATARS).map((record: any) => (
        <ContactAvatar
          key={record.id}
          record={record}
          width={25}
          height={25}
          title={`${record.first_name} ${record.last_name}`}
        />
      ))}
      {total > MAX_AVATARS && (
        <span
          className="relative flex size-8 shrink-0 overflow-hidden rounded-full w-[25px] h-[25px]"
          data-slot="avatar"
        >
          <span className="bg-muted flex size-full items-center justify-center rounded-full text-[10px]">
            +{total - MAX_AVATARS}
          </span>
        </span>
      )}
    </div>
  );
};
